import { type FormEvent, type MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BadgeEuro,
  Boxes,
  Building2,
  Coins,
  Download,
  FileText,
  Link2,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import { germanVatRates } from '@forq/masterdata-data';
import type {
  ArticleStock,
  ArticleSupplierLink,
  MasterdataGateway,
  MasterdataItemKind,
  MasterdataItemPage,
  MasterdataItemRecord,
  MasterdataStatusFilter,
  MasterdataUnit,
  SupplierPage,
  SupplierRecord,
} from '@forq/masterdata-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import type { MastersGateway, MasterEntry } from '@forq/masters-data';
import { confirmDiscard, CustomizableMetrics, describeDeleteOutcome, EmptyState, RecordDeleteButton, SearchSelect, useContextMenu, useRecordDeleteFlow, type ContextMenuItem } from '@forq/ui';
import { renderArticleSheetHtml, renderSupplierSheetHtml } from './documents';

interface MasterdataPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: MasterdataGateway;
  readonly mastersGateway: MastersGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly companyGateway: CompanyProfileGateway;
  /** Lagerstand + Bewegungen je Artikelnummer -- für den „Bestand & Bewegungen"-Tab der Artikelakte. */
  readonly loadStock?: (tenantId: string) => Promise<Readonly<Record<string, ArticleStock>>>;
  /** Lieferanten je Artikelnummer -- für den „Lieferanten"-Tab. */
  readonly loadSuppliers?: (tenantId: string) => Promise<Readonly<Record<string, readonly ArticleSupplierLink[]>>>;
}

type Section = 'articles' | 'services' | 'suppliers';
type ItemEditor = { readonly kind: MasterdataItemKind; readonly item: MasterdataItemRecord | null } | null;

/** Canonical German record-URL base per section — the target for deep links and row selection. */
const sectionRoute: Record<Section, string> = {
  articles: '/artikel',
  services: '/dienstleistungen',
  suppliers: '/lieferanten',
};

const emptyItems: MasterdataItemPage = { items: [], total: 0, page: 1, pageSize: 25 };
const emptySuppliers: SupplierPage = { items: [], total: 0, page: 1, pageSize: 25 };

const unitLabels: Record<MasterdataUnit, string> = {
  piece: 'Stück',
  hour: 'Stunde',
  kilometer: 'Kilometer',
  'flat-rate': 'Pauschale',
  meter: 'Meter',
  kilogram: 'Kilogramm',
  liter: 'Liter',
};

function value(form: FormData, key: string): string {
  return String(form.get(key) ?? '').trim();
}

function numberValue(form: FormData, key: string): number {
  const raw = value(form, key).replace(',', '.');
  return raw ? Number(raw) : 0;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Stammdaten konnten nicht verarbeitet werden.';
}

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function sectionLabel(section: Section): string {
  if (section === 'services') return 'Dienstleistungen';
  if (section === 'suppliers') return 'Lieferanten';
  return 'Artikel';
}

function dateShort(value: string): string { return value ? new Date(value).toLocaleDateString('de-DE') : '–'; }

function csvCell(value: string | number | null): string {
  const text = String(value ?? '');
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const movementTypeLabel: Record<string, string> = {
  receipt: 'Zugang', issue: 'Abgang', transfer: 'Umlagerung', adjustment: 'Korrektur',
};

/** Detail-Tabs der Artikel-/Leistungsakte. Bestand & Lieferanten nur für lagergeführte Artikel. */
function itemTabsFor(item: MasterdataItemRecord): readonly { readonly id: string; readonly label: string }[] {
  const tabs = [{ id: 'overview', label: 'Übersicht' }];
  if (item.kind === 'article') {
    if (item.stockTracked) tabs.push({ id: 'stock', label: 'Bestand & Bewegungen' });
    tabs.push({ id: 'suppliers', label: 'Lieferanten' });
  }
  tabs.push({ id: 'prices', label: 'Preise' });
  tabs.push({ id: 'history', label: 'Historie' });
  return tabs;
}

/** Detail-Tabs der Lieferantenakte. */
const supplierTabs: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'items', label: 'Artikel' },
  { id: 'notes', label: 'Bemerkungen' },
  { id: 'history', label: 'Historie' },
];

export function MasterdataPage({ tenant, mode, permissions, enabledFeatureIds, gateway, mastersGateway, deletionGateway, companyGateway, loadStock, loadSuppliers }: MasterdataPageProps) {
  const availableSections = useMemo(() => ([
    enabledFeatureIds.includes('masterdata.articles') ? 'articles' as const : null,
    enabledFeatureIds.includes('masterdata.services') ? 'services' as const : null,
    enabledFeatureIds.includes('masterdata.suppliers') ? 'suppliers' as const : null,
  ].filter(Boolean) as Section[]), [enabledFeatureIds]);
  const navigate = useNavigate();
  const { nummer } = useParams();
  const [section, setSection] = useState<Section>(availableSections[0] ?? 'articles');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<MasterdataStatusFilter>('all');
  const [bestandFrom, setBestandFrom] = useState('');
  const [bestandTo, setBestandTo] = useState('');
  const [itemPage, setItemPage] = useState<MasterdataItemPage>(emptyItems);
  const [supplierPage, setSupplierPage] = useState<SupplierPage>(emptySuppliers);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [itemDetail, setItemDetail] = useState<MasterdataItemRecord | null>(null);
  const [warengruppen, setWarengruppen] = useState<readonly MasterEntry[]>([]);
  const [supplierDetail, setSupplierDetail] = useState<SupplierRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [itemEditor, setItemEditor] = useState<ItemEditor>(null);
  const [supplierEditor, setSupplierEditor] = useState<SupplierRecord | 'new' | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [articleOptions, setArticleOptions] = useState<readonly MasterdataItemRecord[]>([]);
  const [itemTab, setItemTab] = useState('overview');
  const [viewMode, setViewMode] = useState<'split' | 'table'>('split');
  const [stockMap, setStockMap] = useState<Readonly<Record<string, ArticleStock>>>({});
  const [suppliersMap, setSuppliersMap] = useState<Readonly<Record<string, readonly ArticleSupplierLink[]>>>({});
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [supplierTab, setSupplierTab] = useState('overview');
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const itemKind: MasterdataItemKind = section === 'services' ? 'service' : 'article';
  const canManageItems = section === 'services'
    ? permissions.has('masterdata.services.manage')
    : permissions.has('masterdata.articles.manage');
  const canManageSuppliers = permissions.has('masterdata.suppliers.manage');
  const canViewPurchasePrices = permissions.has('masterdata.purchase_prices.view');

  useEffect(() => {
    if (!availableSections.includes(section)) setSection(availableSections[0] ?? 'articles');
  }, [availableSections, section]);

  useEffect(() => {
    setQuery('');
    setStatus('all');
    setSelectedItemId(null);
    setSelectedSupplierId(null);
    setItemDetail(null);
    setSupplierDetail(null);
  }, [section, tenant.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (section === 'suppliers') {
        const result = await gateway.searchSuppliers({ tenantId: tenant.id, query, status, bestandFrom, bestandTo, pageSize: 25 });
        setSupplierPage(result);
        setSelectedSupplierId((current) => current && result.items.some((item) => item.id === current)
          ? current
          : result.items[0]?.id ?? null);
      } else {
        const result = await gateway.searchItems({ tenantId: tenant.id, kind: itemKind, query, status, bestandFrom, bestandTo, pageSize: 25 });
        setItemPage(result);
        setSelectedItemId((current) => current && result.items.some((item) => item.id === current)
          ? current
          : result.items[0]?.id ?? null);
      }
    } catch (loadError) {
      setError(messageOf(loadError));
      if (section === 'suppliers') setSupplierPage(emptySuppliers);
      else setItemPage(emptyItems);
    } finally {
      setLoading(false);
    }
  }, [gateway, itemKind, query, section, status, bestandFrom, bestandTo, tenant.id]);

  // FORQ-eigenes Rechtsklick-Menü auf Artikel-/Dienstleistungs-/Lieferanten-Zeilen.
  const canDeleteItems = permissions.has('masterdata.articles.delete');
  const canDeleteSuppliers = permissions.has('masterdata.suppliers.delete');
  const { open: openRowMenu, menu: rowContextMenu } = useContextMenu();
  const { requestDelete, deleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const itemMenuItems = useCallback((item: MasterdataItemRecord): ContextMenuItem[] => {
    const noun = item.kind === 'article' ? 'Artikel' : 'Dienstleistung';
    return [
      { label: 'Öffnen', icon: <Package size={15} />, onSelect: () => { setViewMode('split'); navigate(`${sectionRoute[section]}/${item.number}`); } },
      { label: 'Bearbeiten', icon: <Pencil size={15} />, disabled: !canManageItems, onSelect: () => setItemEditor({ kind: item.kind, item }) },
      {
        label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDeleteItems, separatorBefore: true,
        onSelect: () => requestDelete({
          entity: 'article', id: item.id, noun, label: item.name,
          onDone: (outcome) => {
            setNotice(describeDeleteOutcome(outcome, noun, item.name));
            setSelectedItemId((current) => (current === item.id ? null : current));
            setItemDetail((current) => (current?.id === item.id ? null : current));
            void load();
          },
        }),
      },
    ];
  }, [navigate, section, canManageItems, canDeleteItems, requestDelete, load]);
  const supplierMenuItems = useCallback((supplier: SupplierRecord): ContextMenuItem[] => [
    { label: 'Öffnen', icon: <Building2 size={15} />, onSelect: () => { setViewMode('split'); navigate(`${sectionRoute.suppliers}/${supplier.number}`); } },
    { label: 'Bearbeiten', icon: <Pencil size={15} />, disabled: !canManageSuppliers, onSelect: () => setSupplierEditor(supplier) },
    {
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDeleteSuppliers, separatorBefore: true,
      onSelect: () => requestDelete({
        entity: 'supplier', id: supplier.id, noun: 'Lieferant', label: supplier.name,
        onDone: (outcome) => {
          setNotice(describeDeleteOutcome(outcome, 'Lieferant', supplier.name));
          setSelectedSupplierId((current) => (current === supplier.id ? null : current));
          setSupplierDetail((current) => (current?.id === supplier.id ? null : current));
          void load();
        },
      }),
    },
  ], [navigate, canManageSuppliers, canDeleteSuppliers, requestDelete, load]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 160);
    return () => window.clearTimeout(timer);
  }, [load]);

  // Deep-link selection: /artikel/:nummer (or /dienstleistungen, /lieferanten) selects the matching
  // record by its business number once the current section's list is loaded.
  useEffect(() => {
    if (!nummer) return;
    if (section === 'suppliers') {
      const found = supplierPage.items.find((item) => item.number === nummer);
      if (found) setSelectedSupplierId(found.id);
    } else {
      const found = itemPage.items.find((item) => item.number === nummer);
      if (found) setSelectedItemId(found.id);
    }
  }, [nummer, section, itemPage, supplierPage]);

  useEffect(() => {
    if (!selectedItemId || section === 'suppliers') {
      setItemDetail(null);
      return;
    }
    let active = true;
    void gateway.getItem(tenant.id, selectedItemId)
      .then((result) => { if (active) setItemDetail(result); })
      .catch((loadError: unknown) => { if (active) setError(messageOf(loadError)); });
    return () => { active = false; };
  }, [gateway, section, selectedItemId, tenant.id]);

  useEffect(() => {
    if (!selectedSupplierId || section !== 'suppliers') {
      setSupplierDetail(null);
      return;
    }
    let active = true;
    void gateway.getSupplier(tenant.id, selectedSupplierId)
      .then((result) => { if (active) setSupplierDetail(result); })
      .catch((loadError: unknown) => { if (active) setError(messageOf(loadError)); });
    return () => { active = false; };
  }, [gateway, section, selectedSupplierId, tenant.id]);

  // Bestand/Bewegungen + Lieferanten + Firmenprofil einmal je Mandant laden (indexiert über Artikelnummer).
  useEffect(() => {
    if (!loadStock) { setStockMap({}); return undefined; }
    let active = true;
    loadStock(tenant.id).then((map) => { if (active) setStockMap(map); }).catch(() => { if (active) setStockMap({}); });
    return () => { active = false; };
  }, [loadStock, tenant.id]);

  useEffect(() => {
    if (!loadSuppliers) { setSuppliersMap({}); return undefined; }
    let active = true;
    loadSuppliers(tenant.id).then((map) => { if (active) setSuppliersMap(map); }).catch(() => { if (active) setSuppliersMap({}); });
    return () => { active = false; };
  }, [loadSuppliers, tenant.id]);

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [companyGateway, tenant.id]);

  useEffect(() => {
    let active = true;
    mastersGateway.listWarengruppen(tenant.id).then((list) => { if (active) setWarengruppen(list); }).catch(() => { if (active) setWarengruppen([]); });
    return () => { active = false; };
  }, [mastersGateway, tenant.id]);

  // Beim Eintragswechsel auf die Übersicht zurück -- sonst bleibt man auf einem (evtl. ausgeblendeten) Tab.
  useEffect(() => { setItemTab('overview'); }, [itemDetail?.id]);
  useEffect(() => { setSupplierTab('overview'); setNotesEditing(false); }, [supplierDetail?.id]);

  const submitItem = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!itemEditor) return;
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError(null);
    try {
      const saved = await gateway.saveItem({
        tenantId: tenant.id,
        itemId: itemEditor.item?.id,
        kind: itemEditor.kind,
        name: value(form, 'name'),
        description: value(form, 'description'),
        unit: value(form, 'unit') as MasterdataUnit,
        salesPrice: numberValue(form, 'salesPrice'),
        purchasePrice: numberValue(form, 'purchasePrice'),
        vatRate: numberValue(form, 'vatRate'),
        manufacturer: value(form, 'manufacturer'),
        manufacturerPartNumber: value(form, 'manufacturerPartNumber'),
        barcode: value(form, 'barcode'),
        stockTracked: form.get('stockTracked') === 'on',
        minimumStock: numberValue(form, 'minimumStock'),
        active: form.get('active') === 'on',
        warengruppeId: value(form, 'warengruppeId') || null,
        isUvv: form.get('isUvv') === 'on',
      });
      setItemEditor(null);
      setSelectedItemId(saved.id);
      setItemDetail(saved);
      setQuery(saved.number);
      navigate(`${sectionRoute[itemEditor.kind === 'service' ? 'services' : 'articles']}/${saved.number}`);
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [gateway, itemEditor, navigate, tenant.id]);

  const submitSupplier = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supplierEditor) return;
    const form = new FormData(event.currentTarget);
    const existing = supplierEditor === 'new' ? null : supplierEditor;
    setSaving(true);
    setError(null);
    try {
      const saved = await gateway.saveSupplier({
        tenantId: tenant.id,
        supplierId: existing?.id,
        name: value(form, 'name'),
        vatId: value(form, 'vatId'),
        email: value(form, 'email'),
        phone: value(form, 'phone'),
        website: value(form, 'website'),
        contactName: value(form, 'contactName'),
        street: value(form, 'street'),
        postalCode: value(form, 'postalCode'),
        city: value(form, 'city'),
        countryCode: value(form, 'countryCode'),
        notes: value(form, 'notes'),
        active: form.get('active') === 'on',
      });
      setSupplierEditor(null);
      setSelectedSupplierId(saved.id);
      setSupplierDetail(saved);
      setQuery(saved.number);
      navigate(`${sectionRoute.suppliers}/${saved.number}`);
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [gateway, navigate, supplierEditor, tenant.id]);

  const openLink = useCallback(async () => {
    setError(null);
    try {
      setArticleOptions(await gateway.searchArticleOptions(tenant.id));
      setLinkOpen(true);
    } catch (loadError) {
      setError(messageOf(loadError));
    }
  }, [gateway, tenant.id]);

  const submitLink = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supplierDetail) return;
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError(null);
    try {
      const updated = await gateway.linkSupplierItem({
        tenantId: tenant.id,
        supplierId: supplierDetail.id,
        itemId: value(form, 'itemId'),
        supplierItemNumber: value(form, 'supplierItemNumber'),
        purchasePrice: numberValue(form, 'purchasePrice'),
        leadTimeDays: value(form, 'leadTimeDays') ? Number(value(form, 'leadTimeDays')) : null,
        preferred: form.get('preferred') === 'on',
      });
      setSupplierDetail(updated);
      setLinkOpen(false);
      await load();
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setSaving(false);
    }
  }, [gateway, load, supplierDetail, tenant.id]);

  const unlink = useCallback(async (itemId: string) => {
    if (!supplierDetail || !window.confirm('Artikelverknüpfung wirklich entfernen?')) return;
    setSaving(true);
    setError(null);
    try {
      setSupplierDetail(await gateway.unlinkSupplierItem(tenant.id, supplierDetail.id, itemId));
      await load();
    } catch (removeError) {
      setError(messageOf(removeError));
    } finally {
      setSaving(false);
    }
  }, [gateway, load, supplierDetail, tenant.id]);

  const articleStock = itemDetail ? (stockMap[itemDetail.number] ?? null) : null;
  const articleSuppliers = itemDetail ? (suppliersMap[itemDetail.number] ?? []) : [];
  const margin = itemDetail && itemDetail.salesPrice > 0 && itemDetail.purchasePrice > 0
    ? Math.round((itemDetail.salesPrice - itemDetail.purchasePrice) * 100) / 100
    : null;
  const supplierPreferredCount = supplierDetail ? supplierDetail.items.filter((entry) => entry.preferred).length : 0;
  const supplierAvgLead = (() => {
    if (!supplierDetail) return null;
    const leads = supplierDetail.items.map((entry) => entry.leadTimeDays).filter((value): value is number => value !== null);
    return leads.length ? Math.round(leads.reduce((sum, value) => sum + value, 0) / leads.length) : null;
  })();

  // Alle (gefilterten) Einträge des Bereichs als CSV -- zieht die volle Trefferliste, nicht nur die Seite.
  const exportCsv = useCallback(async () => {
    try {
      const all = await gateway.searchItems({ tenantId: tenant.id, kind: itemKind, query, status, bestandFrom, bestandTo, pageSize: 1000 });
      const headers = ['Nummer', 'Bezeichnung', 'Einheit', 'VK', ...(canViewPurchasePrices ? ['EK'] : []), 'USt', 'Hersteller', 'Hersteller-Nr.', 'Barcode', 'Bestand', 'Lieferanten', 'Status', 'Im Bestand seit'];
      const rows = all.items.map((entry) => [
        entry.number, entry.name, unitLabels[entry.unit], entry.salesPrice,
        ...(canViewPurchasePrices ? [entry.purchasePrice] : []),
        `${entry.vatRate} %`, entry.manufacturer, entry.manufacturerPartNumber, entry.barcode,
        stockMap[entry.number]?.onHand ?? '', entry.supplierCount, entry.active ? 'Aktiv' : 'Inaktiv',
        entry.activeSince ? new Date(entry.activeSince).toLocaleDateString('de-DE') : '',
      ]);
      const bom = String.fromCharCode(0xfeff);
      const csv = [headers, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
      const blob = new Blob([`${bom}${csv}`], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${itemKind === 'article' ? 'artikel' : 'leistungen'}-${tenant.numberPrefix}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (exportError) {
      setError(messageOf(exportError));
    }
  }, [gateway, tenant.id, tenant.numberPrefix, itemKind, query, status, bestandFrom, bestandTo, canViewPurchasePrices, stockMap]);

  const exportSupplierCsv = useCallback(async () => {
    try {
      const all = await gateway.searchSuppliers({ tenantId: tenant.id, query, status, bestandFrom, bestandTo, pageSize: 1000 });
      const headers = ['Nummer', 'Name', 'Ansprechpartner', 'E-Mail', 'Telefon', 'Ort', 'USt-ID', 'Artikel', 'Status', 'Im Bestand seit'];
      const rows = all.items.map((entry) => [
        entry.number, entry.name, entry.contactName, entry.email, entry.phone, entry.city, entry.vatId,
        entry.itemCount, entry.active ? 'Aktiv' : 'Inaktiv',
        entry.activeSince ? new Date(entry.activeSince).toLocaleDateString('de-DE') : '',
      ]);
      const bom = String.fromCharCode(0xfeff);
      const csv = [headers, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
      const blob = new Blob([`${bom}${csv}`], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `lieferanten-${tenant.numberPrefix}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (exportError) {
      setError(messageOf(exportError));
    }
  }, [gateway, tenant.id, tenant.numberPrefix, query, status, bestandFrom, bestandTo]);

  // Bemerkungen des Lieferanten inline speichern. Die volle Payload aus dem Datensatz mitschicken;
  // die Artikel-Verknüpfungen liegen separat (supplier_items) und werden von saveSupplier nicht berührt.
  const saveSupplierNotes = useCallback(async () => {
    if (!supplierDetail) return;
    setNotesSaving(true);
    setError(null);
    try {
      const saved = await gateway.saveSupplier({
        tenantId: tenant.id,
        supplierId: supplierDetail.id,
        name: supplierDetail.name,
        vatId: supplierDetail.vatId,
        email: supplierDetail.email,
        phone: supplierDetail.phone,
        website: supplierDetail.website,
        contactName: supplierDetail.contactName,
        street: supplierDetail.street,
        postalCode: supplierDetail.postalCode,
        city: supplierDetail.city,
        countryCode: supplierDetail.countryCode,
        notes: notesDraft,
        active: supplierDetail.active,
      });
      setSupplierDetail(saved);
      setNotesEditing(false);
      await load();
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setNotesSaving(false);
    }
  }, [gateway, supplierDetail, notesDraft, tenant.id, load]);

  if (availableSections.length === 0) {
    return <EmptyState eyebrow="Stammdaten" title="Keine Stammdatenfunktion ist freigegeben." description="Die Bereiche werden über Rollen und Funktionsfreigaben gesteuert." />;
  }

  return (
    <div className="masterdata-page">
      <header className="masterdata-header">
        <div><span className="masterdata-eyebrow">FORQ Master Data</span><h1>{availableSections.length === 1 ? sectionLabel(section) : 'Artikel, Leistungen & Lieferanten'}</h1><p>{tenant.name} · Nummernbereich {tenant.numberPrefix}xxxx</p></div>
        <span className={`masterdata-mode ${mode === 'demo' ? 'is-demo' : ''}`}>{mode === 'demo' ? 'Demo-Daten' : 'Supabase verbunden'}</span>
      </header>

      {availableSections.length > 1 ? (
        <nav className="masterdata-tabs" aria-label="Stammdatenbereiche">
          {availableSections.includes('articles') ? <button type="button" className={section === 'articles' ? 'is-active' : ''} onClick={() => setSection('articles')}><Package size={16} /> Artikel</button> : null}
          {availableSections.includes('services') ? <button type="button" className={section === 'services' ? 'is-active' : ''} onClick={() => setSection('services')}><Wrench size={16} /> Dienstleistungen</button> : null}
          {availableSections.includes('suppliers') ? <button type="button" className={section === 'suppliers' ? 'is-active' : ''} onClick={() => setSection('suppliers')}><Building2 size={16} /> Lieferanten</button> : null}
        </nav>
      ) : null}

      {error && !itemEditor && !supplierEditor && !linkOpen ? <div className="masterdata-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError(null)}>Schließen</button></div> : null}
      {notice ? <div className="masterdata-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Schließen</button></div> : null}

      <section className="masterdata-toolbar">
        <label><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`${sectionLabel(section)} durchsuchen …`} /></label>
        <div className="masterdata-viewtoggle" role="group" aria-label="Ansicht">
          <button type="button" className={viewMode === 'split' ? 'is-active' : ''} onClick={() => setViewMode('split')}>Detail</button>
          <button type="button" className={viewMode === 'table' ? 'is-active' : ''} onClick={() => setViewMode('table')}>Liste</button>
        </div>
        <SearchSelect value={status} onChange={(next) => setStatus(next as MasterdataStatusFilter)} ariaLabel="Status" options={[{ value: 'all', label: 'Alle Status' }, { value: 'active', label: 'Aktiv' }, { value: 'inactive', label: 'Inaktiv' }]} />
        <div className="masterdata-bestand" title="Bestand im Zeitraum"><span>Bestand</span><input type="date" aria-label="Bestand von" value={bestandFrom} onChange={(event) => setBestandFrom(event.target.value)} /><span>–</span><input type="date" aria-label="Bestand bis" value={bestandTo} onChange={(event) => setBestandTo(event.target.value)} />{(bestandFrom || bestandTo) ? <button type="button" className="masterdata-bestand__clear" onClick={() => { setBestandFrom(''); setBestandTo(''); }}>×</button> : null}</div>
        <span>{loading ? 'Lädt …' : `${section === 'suppliers' ? supplierPage.total : itemPage.total} Treffer`}</span>
        <button type="button" className="masterdata-secondary" disabled={section === 'suppliers' ? supplierPage.total === 0 : itemPage.total === 0} onClick={() => void (section === 'suppliers' ? exportSupplierCsv() : exportCsv())} title={`Alle gefilterten ${sectionLabel(section)} als CSV exportieren`}><Download size={15} /> Export</button>
        {section === 'suppliers'
          ? <button type="button" className="masterdata-primary" disabled={!canManageSuppliers} onClick={() => setSupplierEditor('new')}><Plus size={16} /> Lieferant</button>
          : <button type="button" className="masterdata-primary" disabled={!canManageItems} onClick={() => setItemEditor({ kind: itemKind, item: null })}><Plus size={16} /> {itemKind === 'article' ? 'Artikel' : 'Leistung'}</button>}
      </section>

      {viewMode === 'table' ? (
        section === 'suppliers'
          ? <SupplierTable suppliers={supplierPage.items} selectedId={selectedSupplierId} loading={loading} onOpen={(supplier) => { setViewMode('split'); navigate(`${sectionRoute.suppliers}/${supplier.number}`); }} onContext={(event, supplier) => openRowMenu(event, supplierMenuItems(supplier))} />
          : <ItemTable items={itemPage.items} kind={itemKind} selectedId={selectedItemId} loading={loading} canViewPurchasePrices={canViewPurchasePrices} stockMap={stockMap} onOpen={(item) => { setViewMode('split'); navigate(`${sectionRoute[section]}/${item.number}`); }} onContext={(event, item) => openRowMenu(event, itemMenuItems(item))} />
      ) : (
      <div className="masterdata-workspace">
        <aside className="masterdata-list">
          {section !== 'suppliers' ? itemPage.items.map((item) => (
            <button type="button" key={item.id} className={item.id === selectedItemId ? 'is-active' : ''} onClick={() => navigate(`${sectionRoute[section]}/${item.number}`)} onContextMenu={(event) => openRowMenu(event, itemMenuItems(item))}>
              <span className="masterdata-list__icon">{item.kind === 'article' ? <Package size={17} /> : <Wrench size={17} />}</span>
              <span><strong>{item.name}</strong><small>{item.number} · {unitLabels[item.unit]}</small></span>
              <em className={item.active ? 'is-active' : 'is-inactive'}>{item.active ? 'Aktiv' : 'Inaktiv'}</em>
            </button>
          )) : supplierPage.items.map((supplier) => (
            <button type="button" key={supplier.id} className={supplier.id === selectedSupplierId ? 'is-active' : ''} onClick={() => navigate(`${sectionRoute[section]}/${supplier.number}`)} onContextMenu={(event) => openRowMenu(event, supplierMenuItems(supplier))}>
              <span className="masterdata-list__icon"><Building2 size={17} /></span>
              <span><strong>{supplier.name}</strong><small>{supplier.number} · {supplier.city || 'Ort fehlt'}</small></span>
              <em className={supplier.active ? 'is-active' : 'is-inactive'}>{supplier.active ? 'Aktiv' : 'Inaktiv'}</em>
            </button>
          ))}
          {!loading && (section === 'suppliers' ? supplierPage.items.length === 0 : itemPage.items.length === 0) ? <p>Keine Einträge gefunden.</p> : null}
        </aside>

        <main className="masterdata-detail">
          {loading ? <div className="masterdata-loading">Stammdaten werden geladen …</div> : null}
          {!loading && section !== 'suppliers' && !itemDetail ? <EmptyState eyebrow={sectionLabel(section)} title="Wähle einen Eintrag aus." description="Links findest du die Stammdaten des aktiven Mandanten." /> : null}
          {!loading && section === 'suppliers' && !supplierDetail ? <EmptyState eyebrow="Lieferanten" title="Wähle einen Lieferanten aus." description="Links findest du alle Lieferanten des aktiven Mandanten." /> : null}

          {!loading && itemDetail ? (
            <>
              <div className="masterdata-detail__title"><div><span className="masterdata-eyebrow">{itemDetail.kind === 'article' ? 'Artikel' : 'Dienstleistung'} {itemDetail.number}</span><h2>{itemDetail.name}</h2><p>{itemDetail.description || 'Keine Beschreibung hinterlegt.'}</p></div><div className="masterdata-detail__actions"><button type="button" className="masterdata-secondary" onClick={() => setSheetOpen(true)}><FileText size={15} /> {itemDetail.kind === 'article' ? 'Artikelblatt' : 'Leistungsblatt'}</button><button type="button" className="masterdata-secondary" disabled={!canManageItems} onClick={() => setItemEditor({ kind: itemDetail.kind, item: itemDetail })}><Pencil size={15} /> Bearbeiten</button><RecordDeleteButton className="masterdata-secondary is-danger" gateway={deletionGateway} tenantId={tenant.id} entity="article" id={itemDetail.id} noun={itemDetail.kind === 'article' ? 'Artikel' : 'Dienstleistung'} label={itemDetail.name} canDelete={permissions.has('masterdata.articles.delete')} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, itemDetail.kind === 'article' ? 'Artikel' : 'Dienstleistung', itemDetail.name)); setSelectedItemId(null); setItemDetail(null); navigate(sectionRoute[section]); void load(); }} /></div></div>

              <nav className="masterdata-detail-tabs" aria-label="Detail">{itemTabsFor(itemDetail).map((entry) => <button type="button" key={entry.id} className={itemTab === entry.id ? 'is-active' : ''} onClick={() => setItemTab(entry.id)}>{entry.label}</button>)}</nav>

              <div className="masterdata-tab">
                {itemTab === 'overview' ? (
                  <>
                    <div className="masterdata-metrics">
                      <CustomizableMetrics
                        pageKey="article-akte-overview"
                        tiles={[
                          { key: 'salesprice', node: <article><BadgeEuro size={17} /><span>Verkaufspreis</span><strong>{money(itemDetail.salesPrice)}</strong></article> },
                          { key: 'margin', node: <article><TrendingUp size={17} /><span>Marge</span><strong>{margin === null ? '–' : money(margin)}</strong></article> },
                          { key: 'stock-unit', node: itemDetail.kind === 'article' ? <article className={articleStock?.lowStock ? 'is-warn' : ''}><Boxes size={17} /><span>Bestand</span><strong>{articleStock ? `${articleStock.onHand} ${unitLabels[itemDetail.unit]}` : (itemDetail.stockTracked ? '0' : '–')}</strong></article> : <article><Boxes size={17} /><span>Einheit</span><strong>{unitLabels[itemDetail.unit]}</strong></article> },
                          { key: 'suppliers', node: <article><Building2 size={17} /><span>Lieferanten</span><strong>{itemDetail.supplierCount}</strong></article> },
                        ]}
                      />
                    </div>
                    <section className="masterdata-card-grid">
                      {itemDetail.kind === 'article' ? <article><h3>Artikelinformationen</h3><dl><div><dt>Hersteller</dt><dd>{itemDetail.manufacturer || '—'}</dd></div><div><dt>Hersteller-Nr.</dt><dd>{itemDetail.manufacturerPartNumber || '—'}</dd></div><div><dt>Barcode</dt><dd>{itemDetail.barcode || '—'}</dd></div><div><dt>Bestandsführung</dt><dd>{itemDetail.stockTracked ? `Ja · Mindestbestand ${itemDetail.minimumStock} ${unitLabels[itemDetail.unit]}` : 'Nein'}</dd></div></dl></article> : null}
                      <article><h3>Eckdaten</h3><dl><div><dt>Einheit</dt><dd>{unitLabels[itemDetail.unit]}</dd></div><div><dt>Umsatzsteuer</dt><dd>{itemDetail.vatRate} %</dd></div><div><dt>Bevorzugter Lieferant</dt><dd>{itemDetail.preferredSupplierName || '—'}</dd></div><div><dt>Warengruppe</dt><dd>{warengruppen.find((entry) => entry.id === itemDetail.warengruppeId)?.name ?? itemDetail.warengruppeName ?? '—'}</dd></div><div><dt>Status</dt><dd>{itemDetail.active ? 'Aktiv' : 'Inaktiv'}</dd></div></dl></article>
                    </section>
                  </>
                ) : null}

                {itemTab === 'stock' ? (
                  articleStock && (articleStock.locations.length > 0 || articleStock.movements.length > 0) ? (
                    <>
                      <div className="masterdata-metrics">
                        <CustomizableMetrics
                          pageKey="article-akte-stock"
                          tiles={[
                            { key: 'total', node: <article className={articleStock.lowStock ? 'is-warn' : ''}><Boxes size={17} /><span>Bestand gesamt</span><strong>{articleStock.onHand} {unitLabels[itemDetail.unit]}</strong></article> },
                            { key: 'available', node: <article><Package size={17} /><span>Verfügbar</span><strong>{articleStock.available} {unitLabels[itemDetail.unit]}</strong></article> },
                            { key: 'reserved', node: <article><Package size={17} /><span>Reserviert</span><strong>{articleStock.reserved} {unitLabels[itemDetail.unit]}</strong></article> },
                            { key: 'minimum', node: <article><Boxes size={17} /><span>Mindestbestand</span><strong>{itemDetail.minimumStock} {unitLabels[itemDetail.unit]}</strong></article> },
                          ]}
                        />
                      </div>
                      {articleStock.locations.length > 0 ? (
                        <section className="masterdata-supplier-items"><div><span className="masterdata-eyebrow">Lager</span><h3>Bestand je Lagerplatz</h3></div><div className="masterdata-table-wrap"><table><thead><tr><th>Lagerplatz</th><th>Bestand</th><th>Verfügbar</th><th>Mindest</th><th>Status</th></tr></thead><tbody>{articleStock.locations.map((location) => <tr key={location.locationCode}><td><strong>{location.locationName}</strong><small>{location.locationCode}</small></td><td>{location.onHand}</td><td>{location.available}</td><td>{location.minimumStock}</td><td>{location.lowStock ? <span className="masterdata-lowstock">Unter Mindest</span> : 'OK'}</td></tr>)}</tbody></table></div></section>
                      ) : null}
                      {articleStock.movements.length > 0 ? (
                        <section className="masterdata-supplier-items"><div><span className="masterdata-eyebrow">Bewegungen</span><h3>Letzte Lagerbewegungen</h3></div><div className="masterdata-table-wrap"><table><thead><tr><th>Datum</th><th>Art</th><th>Menge</th><th>Von → Nach</th><th>Grund</th></tr></thead><tbody>{articleStock.movements.slice(0, 25).map((movement, index) => <tr key={`${movement.date}:${index}`}><td>{dateShort(movement.date)}</td><td><span className={`masterdata-movement is-${movement.type}`}>{movementTypeLabel[movement.type] ?? movement.type}</span></td><td>{movement.quantity} {unitLabels[itemDetail.unit]}</td><td>{[movement.from || '—', movement.to || '—'].join(' → ')}</td><td>{movement.reason || '—'}{movement.reference ? <small>{movement.reference}</small> : null}</td></tr>)}</tbody></table></div></section>
                      ) : null}
                    </>
                  ) : <p className="masterdata-muted">Für diesen Artikel ist noch kein Lagerbestand erfasst.</p>
                ) : null}

                {itemTab === 'suppliers' ? (
                  articleSuppliers.length === 0
                    ? <p className="masterdata-muted">Diesem Artikel ist noch kein Lieferant zugeordnet. Die Zuordnung erfolgt im Lieferantenstamm über „Artikel verknüpfen".</p>
                    : <div className="masterdata-table-wrap"><table><thead><tr><th>Lieferant</th><th>Lieferanten-Nr.</th><th>Preis</th><th>Lieferzeit</th><th>Bevorzugt</th></tr></thead><tbody>{articleSuppliers.map((link) => <tr key={link.supplierNumber}><td><strong>{link.supplierName}</strong><small>{link.supplierNumber}</small></td><td>{link.supplierItemNumber || '—'}</td><td>{canViewPurchasePrices ? money(link.purchasePrice) : 'Geschützt'}</td><td>{link.leadTimeDays === null ? '—' : `${link.leadTimeDays} Tage`}</td><td>{link.preferred ? 'Ja' : 'Nein'}</td></tr>)}</tbody></table></div>
                ) : null}

                {itemTab === 'prices' ? (
                  <div className="masterdata-metrics">
                    <CustomizableMetrics
                      pageKey="article-akte-prices"
                      tiles={[
                        ...(canViewPurchasePrices && itemDetail.kind === 'article' ? [{ key: 'purchaseprice', node: <article><Coins size={17} /><span>Einkaufspreis</span><strong>{money(itemDetail.purchasePrice)}</strong></article> }] : []),
                        { key: 'salesprice', node: <article><BadgeEuro size={17} /><span>Verkaufspreis</span><strong>{money(itemDetail.salesPrice)}</strong></article> },
                        { key: 'margin', node: <article><TrendingUp size={17} /><span>Marge</span><strong>{margin === null ? '–' : money(margin)}</strong></article> },
                        { key: 'vat', node: <article><Package size={17} /><span>Umsatzsteuer</span><strong>{itemDetail.vatRate} %</strong></article> },
                      ]}
                    />
                  </div>
                ) : null}

                {itemTab === 'history' ? (
                  <dl className="masterdata-history">
                    <div><dt>Im Bestand seit</dt><dd>{dateShort(itemDetail.activeSince)}</dd></div>
                    {itemDetail.activeUntil ? <div><dt>Abgang</dt><dd>{dateShort(itemDetail.activeUntil)}</dd></div> : null}
                    <div><dt>Angelegt</dt><dd>{new Date(itemDetail.createdAt).toLocaleString('de-DE')}</dd></div>
                    <div><dt>Zuletzt geändert</dt><dd>{new Date(itemDetail.updatedAt).toLocaleString('de-DE')}</dd></div>
                  </dl>
                ) : null}
              </div>
            </>
          ) : null}

          {!loading && supplierDetail ? (
            <>
              <div className="masterdata-detail__title"><div><span className="masterdata-eyebrow">Lieferant {supplierDetail.number}</span><h2>{supplierDetail.name}</h2><p>{supplierDetail.contactName || 'Kein Ansprechpartner'} · {supplierDetail.city || 'Ort fehlt'}</p></div><div className="masterdata-detail__actions"><button type="button" className="masterdata-secondary" onClick={() => setSupplierSheetOpen(true)}><FileText size={15} /> Lieferantenblatt</button><button type="button" className="masterdata-secondary" disabled={!canManageSuppliers} onClick={() => setSupplierEditor(supplierDetail)}><Pencil size={15} /> Bearbeiten</button><RecordDeleteButton className="masterdata-secondary is-danger" gateway={deletionGateway} tenantId={tenant.id} entity="supplier" id={supplierDetail.id} noun="Lieferant" label={supplierDetail.name} canDelete={permissions.has('masterdata.suppliers.delete')} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, 'Lieferant', supplierDetail.name)); setSelectedSupplierId(null); setSupplierDetail(null); navigate('/lieferanten'); void load(); }} /></div></div>

              <nav className="masterdata-detail-tabs" aria-label="Detail">{supplierTabs.map((entry) => <button type="button" key={entry.id} className={supplierTab === entry.id ? 'is-active' : ''} onClick={() => setSupplierTab(entry.id)}>{entry.label}</button>)}</nav>

              <div className="masterdata-tab">
                {supplierTab === 'overview' ? (
                  <>
                    <div className="masterdata-metrics">
                      <CustomizableMetrics
                        pageKey="supplier-akte-overview"
                        tiles={[
                          { key: 'items', node: <article><Package size={17} /><span>Verknüpfte Artikel</span><strong>{supplierDetail.itemCount}</strong></article> },
                          { key: 'preferred', node: <article><Link2 size={17} /><span>Bevorzugt</span><strong>{supplierPreferredCount}</strong></article> },
                          { key: 'leadtime', node: <article><Boxes size={17} /><span>Ø Lieferzeit</span><strong>{supplierAvgLead === null ? '–' : `${supplierAvgLead} Tage`}</strong></article> },
                          { key: 'status', node: <article><Building2 size={17} /><span>Status</span><strong>{supplierDetail.active ? 'Aktiv' : 'Inaktiv'}</strong></article> },
                        ]}
                      />
                    </div>
                    <section className="masterdata-card-grid">
                      <article><h3>Kontakt</h3><dl><div><dt>Ansprechpartner</dt><dd>{supplierDetail.contactName || '—'}</dd></div><div><dt>E-Mail</dt><dd>{supplierDetail.email || '—'}</dd></div><div><dt>Telefon</dt><dd>{supplierDetail.phone || '—'}</dd></div><div><dt>Website</dt><dd>{supplierDetail.website || '—'}</dd></div><div><dt>USt-ID</dt><dd>{supplierDetail.vatId || '—'}</dd></div></dl></article>
                      <article><h3>Anschrift</h3><p>{supplierDetail.street || '—'}<br />{supplierDetail.postalCode} {supplierDetail.city}<br />{supplierDetail.countryCode}</p></article>
                    </section>
                  </>
                ) : null}

                {supplierTab === 'items' ? (
                  <section className="masterdata-supplier-items"><div><span className="masterdata-eyebrow">Beschaffung</span><h3>Lieferantenartikel</h3><button type="button" className="masterdata-secondary" disabled={!canManageSuppliers} onClick={() => void openLink()}><Link2 size={15} /> Artikel verknüpfen</button></div><div className="masterdata-table-wrap"><table><thead><tr><th>Artikel</th><th>Lieferanten-Nr.</th><th>Preis</th><th>Lieferzeit</th><th>Bevorzugt</th><th /></tr></thead><tbody>{supplierDetail.items.map((item) => <tr key={item.itemId}><td><strong>{item.itemName}</strong><small>{item.itemNumber}</small></td><td>{item.supplierItemNumber || '—'}</td><td>{canViewPurchasePrices ? money(item.purchasePrice) : 'Geschützt'}</td><td>{item.leadTimeDays === null ? '—' : `${item.leadTimeDays} Tage`}</td><td>{item.preferred ? 'Ja' : 'Nein'}</td><td><button type="button" disabled={!canManageSuppliers || saving} onClick={() => void unlink(item.itemId)}><Trash2 size={14} /></button></td></tr>)}{supplierDetail.items.length === 0 ? <tr><td colSpan={6}>Noch keine Artikel verknüpft.</td></tr> : null}</tbody></table></div></section>
                ) : null}

                {supplierTab === 'notes' ? (
                  <section className="masterdata-notes masterdata-notes--editable">
                    <div className="masterdata-notes__head">
                      <span>Bemerkungen</span>
                      {!notesEditing && canManageSuppliers ? <button type="button" className="masterdata-secondary" onClick={() => { setNotesDraft(supplierDetail.notes); setNotesEditing(true); }}><Pencil size={14} /> Bearbeiten</button> : null}
                    </div>
                    {notesEditing ? (
                      <div className="masterdata-notes__edit">
                        <textarea rows={5} value={notesDraft} onChange={(event) => setNotesDraft(event.target.value)} placeholder="Bemerkungen zum Lieferanten …" />
                        <div className="masterdata-notes__actions">
                          <button type="button" className="masterdata-secondary" onClick={() => { setNotesDraft(supplierDetail.notes); setNotesEditing(false); }} disabled={notesSaving}>Abbrechen</button>
                          <button type="button" className="masterdata-primary" onClick={() => void saveSupplierNotes()} disabled={notesSaving}>{notesSaving ? 'Speichert …' : 'Speichern'}</button>
                        </div>
                      </div>
                    ) : (
                      <p>{supplierDetail.notes || 'Keine Bemerkungen hinterlegt.'}</p>
                    )}
                  </section>
                ) : null}

                {supplierTab === 'history' ? (
                  <dl className="masterdata-history">
                    <div><dt>Im Bestand seit</dt><dd>{dateShort(supplierDetail.activeSince)}</dd></div>
                    {supplierDetail.activeUntil ? <div><dt>Abgang</dt><dd>{dateShort(supplierDetail.activeUntil)}</dd></div> : null}
                    <div><dt>Angelegt</dt><dd>{new Date(supplierDetail.createdAt).toLocaleString('de-DE')}</dd></div>
                    <div><dt>Zuletzt geändert</dt><dd>{new Date(supplierDetail.updatedAt).toLocaleString('de-DE')}</dd></div>
                  </dl>
                ) : null}
              </div>
            </>
          ) : null}
        </main>
      </div>
      )}

      {itemEditor ? <ItemDialog editor={itemEditor} warengruppen={warengruppen} saving={saving} error={error} canViewPurchasePrices={canViewPurchasePrices} onClose={() => setItemEditor(null)} onSubmit={submitItem} /> : null}
      {supplierEditor ? <SupplierDialog supplier={supplierEditor === 'new' ? null : supplierEditor} saving={saving} error={error} onClose={() => setSupplierEditor(null)} onSubmit={submitSupplier} /> : null}
      {linkOpen && supplierDetail ? <LinkDialog articles={articleOptions} saving={saving} error={error} onClose={() => setLinkOpen(false)} onSubmit={submitLink} /> : null}
      {sheetOpen && itemDetail ? <ArticleSheetModal item={itemDetail} stock={articleStock} suppliers={articleSuppliers} company={company} canViewPurchasePrices={canViewPurchasePrices} onClose={() => setSheetOpen(false)} /> : null}
      {supplierSheetOpen && supplierDetail ? <SupplierSheetModal supplier={supplierDetail} company={company} canViewPurchasePrices={canViewPurchasePrices} onClose={() => setSupplierSheetOpen(false)} /> : null}
      {rowContextMenu}
      {deleteDialog}
    </div>
  );
}

function ItemDialog({ editor, warengruppen, saving, error, canViewPurchasePrices, onClose, onSubmit }: { readonly editor: NonNullable<ItemEditor>; readonly warengruppen: readonly MasterEntry[]; readonly saving: boolean; readonly error: string | null; readonly canViewPurchasePrices: boolean; readonly onClose: () => void; readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  const item = editor.item;
  return <div className="masterdata-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}><form className="masterdata-modal__panel" onSubmit={onSubmit}><span className="masterdata-eyebrow">{item ? item.number : 'Neuer Stammdatensatz'}</span><h2>{item ? `${item.name} bearbeiten` : editor.kind === 'article' ? 'Artikel anlegen' : 'Dienstleistung anlegen'}</h2><div className="masterdata-form-grid"><label>Bezeichnung<input name="name" defaultValue={item?.name ?? ''} required autoFocus /></label><label>Einheit<SearchSelect name="unit" defaultValue={item?.unit ?? (editor.kind === 'article' ? 'piece' : 'hour')} options={Object.entries(unitLabels).map(([value, label]) => ({ value, label }))} /></label><label>Verkaufspreis<input name="salesPrice" type="number" min={0} step="0.01" defaultValue={item?.salesPrice ?? 0} /></label>{editor.kind === 'article' && canViewPurchasePrices ? <label>Einkaufspreis<input name="purchasePrice" type="number" min={0} step="0.01" defaultValue={item?.purchasePrice ?? 0} /></label> : null}<label>Umsatzsteuer<SearchSelect name="vatRate" defaultValue={String(item?.vatRate ?? 19)} options={germanVatRates.map((rate) => ({ value: String(rate), label: `${rate} %` }))} /></label><label>Warengruppe<SearchSelect name="warengruppeId" defaultValue={item?.warengruppeId ?? ''} options={[{ value: '', label: 'Keine' }, ...warengruppen.map((entry) => ({ value: entry.id, label: entry.name }))]} /></label>{editor.kind === 'article' ? <><label>Hersteller<input name="manufacturer" defaultValue={item?.manufacturer ?? ''} /></label><label>Hersteller-Nr.<input name="manufacturerPartNumber" defaultValue={item?.manufacturerPartNumber ?? ''} /></label><label>Barcode<input name="barcode" defaultValue={item?.barcode ?? ''} /></label><label>Mindestbestand<input name="minimumStock" type="number" min={0} step="0.01" defaultValue={item?.minimumStock ?? 0} /></label><label className="masterdata-check"><input name="stockTracked" type="checkbox" defaultChecked={item?.stockTracked ?? true} /> Bestand führen</label></> : null}{editor.kind === 'service' ? <label className="masterdata-check"><input name="isUvv" type="checkbox" defaultChecked={item?.isUvv ?? false} /> UVV-Prüfung</label> : null}<label className="masterdata-check"><input name="active" type="checkbox" defaultChecked={item?.active ?? true} /> Aktiv</label><label className="is-wide">Beschreibung<textarea name="description" rows={4} defaultValue={item?.description ?? ''} /></label></div>{error ? <div className="masterdata-modal__error" role="alert">{error}</div> : null}<div className="masterdata-modal__actions"><button type="button" className="masterdata-secondary" onClick={onClose}>Abbrechen</button><button type="submit" className="masterdata-primary" disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button></div></form></div>;
}

function SupplierDialog({ supplier, saving, error, onClose, onSubmit }: { readonly supplier: SupplierRecord | null; readonly saving: boolean; readonly error: string | null; readonly onClose: () => void; readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return <div className="masterdata-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}><form className="masterdata-modal__panel" onSubmit={onSubmit}><span className="masterdata-eyebrow">{supplier?.number ?? 'Neuer Lieferant'}</span><h2>{supplier ? `${supplier.name} bearbeiten` : 'Lieferant anlegen'}</h2><div className="masterdata-form-grid"><label>Name<input name="name" defaultValue={supplier?.name ?? ''} required autoFocus /></label><label>USt-ID<input name="vatId" defaultValue={supplier?.vatId ?? ''} /></label><label>Ansprechpartner<input name="contactName" defaultValue={supplier?.contactName ?? ''} /></label><label>E-Mail<input name="email" type="email" defaultValue={supplier?.email ?? ''} /></label><label>Telefon<input name="phone" defaultValue={supplier?.phone ?? ''} /></label><label>Website<input name="website" defaultValue={supplier?.website ?? ''} /></label><label>Straße<input name="street" defaultValue={supplier?.street ?? ''} /></label><label>PLZ<input name="postalCode" defaultValue={supplier?.postalCode ?? ''} /></label><label>Ort<input name="city" defaultValue={supplier?.city ?? ''} /></label><label>Ländercode<input name="countryCode" maxLength={2} defaultValue={supplier?.countryCode ?? 'DE'} /></label><label className="masterdata-check"><input name="active" type="checkbox" defaultChecked={supplier?.active ?? true} /> Aktiv</label><label className="is-wide">Notizen<textarea name="notes" rows={4} defaultValue={supplier?.notes ?? ''} /></label></div>{error ? <div className="masterdata-modal__error" role="alert">{error}</div> : null}<div className="masterdata-modal__actions"><button type="button" className="masterdata-secondary" onClick={onClose}>Abbrechen</button><button type="submit" className="masterdata-primary" disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button></div></form></div>;
}

function LinkDialog({ articles, saving, error, onClose, onSubmit }: { readonly articles: readonly MasterdataItemRecord[]; readonly saving: boolean; readonly error: string | null; readonly onClose: () => void; readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return <div className="masterdata-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onClose(); }}><form className="masterdata-modal__panel is-small" onSubmit={onSubmit}><span className="masterdata-eyebrow">Lieferantenartikel</span><h2>Artikel verknüpfen</h2><div className="masterdata-form-grid"><label className="is-wide">Artikel<SearchSelect name="itemId" defaultValue="" placeholder="Artikel auswählen …" options={articles.map((article) => ({ value: article.id, label: `${article.number} · ${article.name}` }))} /></label><label>Lieferanten-Artikelnummer<input name="supplierItemNumber" /></label><label>Einkaufspreis<input name="purchasePrice" type="number" min={0} step="0.01" /></label><label>Lieferzeit in Tagen<input name="leadTimeDays" type="number" min={0} max={3650} /></label><label className="masterdata-check"><input name="preferred" type="checkbox" /> Bevorzugter Lieferant</label></div>{error ? <div className="masterdata-modal__error" role="alert">{error}</div> : null}<div className="masterdata-modal__actions"><button type="button" className="masterdata-secondary" onClick={onClose}>Abbrechen</button><button type="submit" className="masterdata-primary" disabled={saving || articles.length === 0}>{saving ? 'Speichert …' : 'Verknüpfen'}</button></div></form></div>;
}

function ItemTable({ items, kind, selectedId, loading, canViewPurchasePrices, stockMap, onOpen, onContext }: {
  readonly items: readonly MasterdataItemRecord[];
  readonly kind: MasterdataItemKind;
  readonly selectedId: string | null;
  readonly loading: boolean;
  readonly canViewPurchasePrices: boolean;
  readonly stockMap: Readonly<Record<string, ArticleStock>>;
  readonly onOpen: (item: MasterdataItemRecord) => void;
  readonly onContext?: (event: ReactMouseEvent, item: MasterdataItemRecord) => void;
}) {
  if (!loading && items.length === 0) return <p className="masterdata-muted">Keine Einträge gefunden.</p>;
  return (
    <div className="masterdata-table-wrap masterdata-list-table">
      <table>
        <thead><tr><th>Nr.</th><th>Bezeichnung</th><th>Einheit</th><th>VK</th>{canViewPurchasePrices && kind === 'article' ? <th>EK</th> : null}<th>USt</th>{kind === 'article' ? <th>Bestand</th> : null}<th>Lieferanten</th><th>Status</th></tr></thead>
        <tbody>
          {items.map((item) => {
            const stock = stockMap[item.number];
            return (
              <tr key={item.id} className={item.id === selectedId ? 'is-active' : ''} onClick={() => onOpen(item)} onContextMenu={onContext ? (event) => onContext(event, item) : undefined}>
                <td className="masterdata-table__nr">{item.number}</td>
                <td><strong>{item.name}</strong></td>
                <td>{unitLabels[item.unit]}</td>
                <td>{money(item.salesPrice)}</td>
                {canViewPurchasePrices && kind === 'article' ? <td>{money(item.purchasePrice)}</td> : null}
                <td>{item.vatRate} %</td>
                {kind === 'article' ? <td className={stock?.lowStock ? 'masterdata-lowstock' : ''}>{item.stockTracked ? (stock ? stock.onHand : 0) : '–'}</td> : null}
                <td>{item.supplierCount}</td>
                <td><em className={item.active ? 'is-active' : 'is-inactive'}>{item.active ? 'Aktiv' : 'Inaktiv'}</em></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ArticleSheetModal({ item, stock, suppliers, company, canViewPurchasePrices, onClose }: {
  readonly item: MasterdataItemRecord;
  readonly stock: ArticleStock | null;
  readonly suppliers: readonly ArticleSupplierLink[];
  readonly company: TenantCompanyProfile;
  readonly canViewPurchasePrices: boolean;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => renderArticleSheetHtml({
    company,
    todayIso: new Date().toISOString().slice(0, 10),
    canViewPurchasePrices,
    item: {
      isService: item.kind === 'service',
      number: item.number,
      name: item.name,
      description: item.description,
      unit: unitLabels[item.unit],
      salesPrice: item.salesPrice,
      purchasePrice: item.purchasePrice,
      vatRate: item.vatRate,
      manufacturer: item.manufacturer,
      manufacturerPartNumber: item.manufacturerPartNumber,
      barcode: item.barcode,
      stockTracked: item.stockTracked,
      minimumStock: item.minimumStock,
      active: item.active,
      activeSince: item.activeSince,
      preferredSupplierName: item.preferredSupplierName,
    },
    stock: stock ? {
      onHand: stock.onHand, available: stock.available, reserved: stock.reserved,
      locations: stock.locations.map((location) => ({ code: location.locationCode, name: location.locationName, onHand: location.onHand, available: location.available, minimumStock: location.minimumStock })),
    } : null,
    suppliers: suppliers.map((link) => ({ supplierNumber: link.supplierNumber, supplierName: link.supplierName, supplierItemNumber: link.supplierItemNumber, purchasePrice: link.purchasePrice, leadTimeDays: link.leadTimeDays, preferred: link.preferred })),
  }), [item, stock, suppliers, company, canViewPurchasePrices]);
  return (
    <div className="masterdata-doc-modal" role="dialog" aria-modal="true" aria-label={item.kind === 'article' ? 'Artikelblatt' : 'Leistungsblatt'} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="masterdata-doc-modal__panel">
        <header><span>{item.kind === 'article' ? 'Artikelblatt' : 'Leistungsblatt'} · {item.name}</span><button type="button" className="masterdata-secondary" onClick={onClose}>Schließen</button></header>
        <iframe className="masterdata-doc-frame" title={item.kind === 'article' ? 'Artikelblatt' : 'Leistungsblatt'} srcDoc={html} />
      </div>
    </div>
  );
}

function SupplierTable({ suppliers, selectedId, loading, onOpen, onContext }: {
  readonly suppliers: readonly SupplierRecord[];
  readonly selectedId: string | null;
  readonly loading: boolean;
  readonly onOpen: (supplier: SupplierRecord) => void;
  readonly onContext?: (event: ReactMouseEvent, supplier: SupplierRecord) => void;
}) {
  if (!loading && suppliers.length === 0) return <p className="masterdata-muted">Keine Lieferanten gefunden.</p>;
  return (
    <div className="masterdata-table-wrap masterdata-list-table">
      <table>
        <thead><tr><th>Nr.</th><th>Name</th><th>Ansprechpartner</th><th>Ort</th><th>Telefon</th><th>Artikel</th><th>Status</th></tr></thead>
        <tbody>
          {suppliers.map((supplier) => (
            <tr key={supplier.id} className={supplier.id === selectedId ? 'is-active' : ''} onClick={() => onOpen(supplier)} onContextMenu={onContext ? (event) => onContext(event, supplier) : undefined}>
              <td className="masterdata-table__nr">{supplier.number}</td>
              <td><strong>{supplier.name}</strong></td>
              <td>{supplier.contactName || '—'}</td>
              <td>{supplier.city || '—'}</td>
              <td>{supplier.phone || '—'}</td>
              <td>{supplier.itemCount}</td>
              <td><em className={supplier.active ? 'is-active' : 'is-inactive'}>{supplier.active ? 'Aktiv' : 'Inaktiv'}</em></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SupplierSheetModal({ supplier, company, canViewPurchasePrices, onClose }: {
  readonly supplier: SupplierRecord;
  readonly company: TenantCompanyProfile;
  readonly canViewPurchasePrices: boolean;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => renderSupplierSheetHtml({
    company,
    todayIso: new Date().toISOString().slice(0, 10),
    canViewPurchasePrices,
    supplier: {
      number: supplier.number,
      name: supplier.name,
      contactName: supplier.contactName,
      email: supplier.email,
      phone: supplier.phone,
      website: supplier.website,
      vatId: supplier.vatId,
      street: supplier.street,
      postalCode: supplier.postalCode,
      city: supplier.city,
      countryCode: supplier.countryCode,
      active: supplier.active,
      activeSince: supplier.activeSince,
      notes: supplier.notes,
    },
    items: supplier.items.map((link) => ({ itemNumber: link.itemNumber, itemName: link.itemName, supplierItemNumber: link.supplierItemNumber, purchasePrice: link.purchasePrice, leadTimeDays: link.leadTimeDays, preferred: link.preferred })),
  }), [supplier, company, canViewPurchasePrices]);
  return (
    <div className="masterdata-doc-modal" role="dialog" aria-modal="true" aria-label="Lieferantenblatt" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="masterdata-doc-modal__panel">
        <header><span>Lieferantenblatt · {supplier.name}</span><button type="button" className="masterdata-secondary" onClick={onClose}>Schließen</button></header>
        <iframe className="masterdata-doc-frame" title="Lieferantenblatt" srcDoc={html} />
      </div>
    </div>
  );
}
